import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Select, RNLLoader, Modal, Btn, useToast } from '../UI';
import { errorText } from '../../utils';
import * as api from '../../api';
import { ActionIcon, EyeIcon, PencilIcon, RecallIcon } from './ActionIcons';
import { SortTh, useSortState, sortRows } from './SortableHeader';
import Pagination from './Pagination';
import PRDetailsView from './PRDetailsView';
import RequisitionExpandedDetail from './RequisitionExpandedDetail';
import { PinnedDetail, useScrollViewport } from './PinnedDetail';

const fmt = (v) =>
  v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—';

// Shared by the two MD/DMD-only, system-wide reporting tabs:
//   mode="approved"         -> every approved PR, across all requesters/departments
//   mode="rejected-returned" -> every PR rejected or returned specifically at the Approval Queue
export default function ApprovalOutcomeRequisitions({ mode, openPrId, onOpenPr }) {
  const isRejectedReturned = mode === 'rejected-returned';

  const [loading, setLoading] = useState(true);
  const [prs, setPrs] = useState([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchVersion, setSearchVersion] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('');
  // Both of these tabs report on a finished requisition, so the expanded row
  // is read-only — Section 1, Section 2 and the line items, with none of the
  // Approve/Return/Hold/Reject controls the live queues carry.
  const [expandedRows, setExpandedRows] = useState({});
  // Which requisition is open, and whether it is open for editing. Controlled
  // when the caller supplies openPrId, uncontrolled otherwise — the aliases
  // below mean every reference in this file is untouched either way.
  const [viewingPrIdState, setViewingPrIdState] = useState(null);
  const [viewingInstanceId, setViewingInstanceId] = useState(null);
  const viewingPrId = openPrId !== undefined ? openPrId : viewingPrIdState;
  const setViewingPrId = onOpenPr || setViewingPrIdState;
  // Confirmation before recalling — reopening a decision somebody made is not
  // something to do on a stray click.
  const [recallTarget, setRecallTarget] = useState(null);
  const [recalling, setRecalling] = useState(false);
  // Shown after a successful recall. A toast is too easy to miss for
  // something this consequential, and the reader needs the rejection comment
  // in front of them to paste into the Return dialog.
  const [recallDone, setRecallDone] = useState(null);
  // Recall of an APPROVED requisition is a different animal from recalling a
  // rejected one: it may first have to delete payment vouchers. So it is
  // preview-then-confirm, and kept in its own state rather than overloading
  // recallTarget — one of these flows deletes financial records and the other
  // does not, and they should not be able to be confused for each other.
  const [approvedTarget, setApprovedTarget] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  // Ticked by hand when the preview reports a side effect that reaches beyond
  // this requisition. Reset every time the modal opens.
  const [acceptedWarnings, setAcceptedWarnings] = useState(false);
  const toast = useToast();
  const sortState = useSortState();
  // Server-side paging — see Pagination. 10 to start.
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  // Caps the expanded row at the visible width so the line items
  // scroll on their own instead of dragging the whole panel.
  const viewport = useScrollViewport([loading]);
  const navigate = useNavigate();

  const openApprovedRecall = async (pr) => {
    setApprovedTarget(pr);
    setPlan(null);
    setAcceptedWarnings(false);
    setPlanLoading(true);
    try {
      setPlan(await api.previewRecallApproved(pr.id || pr.purchase_request_id));
    } catch (e) {
      toast.show(errorText(e), 'error');
      setApprovedTarget(null);
    } finally {
      setPlanLoading(false);
    }
  };

  const recallApproved = async () => {
    if (!approvedTarget) return;
    setRecalling(true);
    try {
      const res = await api.recallApproved(
        approvedTarget.id || approvedTarget.purchase_request_id,
      );
      toast.show(res?.message || 'Recalled into the Approval Queue.', 'success');
      setRecallDone({
        pr_number: res?.pr_number || approvedTarget.pr_number,
        title: approvedTarget.title,
        step_name: res?.step_name,
        vouchers_deleted: res?.vouchers_deleted || [],
      });
      setApprovedTarget(null);
      setPlan(null);
      await fetchPRs();
    } catch (e) {
      toast.show(errorText(e), 'error');
    } finally {
      setRecalling(false);
    }
  };

  const recall = async () => {
    if (!recallTarget) return;
    setRecalling(true);
    try {
      const res = await api.reverseRejection(recallTarget.workflow_instance_id);
      // Both, deliberately: the toast confirms it landed even if the modal is
      // dismissed straight away, and the modal carries the rejection comment
      // the toast has no room for.
      toast.show(res?.message || 'Recalled into the queue.', 'success');
      setRecallDone({
        pr_number: recallTarget.pr_number,
        title: recallTarget.title,
        step_name: res?.step_name,
        original_comment: res?.original_comment || recallTarget.outcome_reason,
      });
      setRecallTarget(null);
      await fetchPRs();
    } catch (e) {
      toast.show(errorText(e), 'error');
    } finally {
      setRecalling(false);
    }
  };

  const fetchPRs = useCallback(async () => {
    setLoading(true);
    try {
      api.clearApiCache();
      const res = isRejectedReturned
        ? await api.getPrsRejectedReturnedByQueue('approval-queue', null, page.skip, page.limit, appliedSearch)
        : await api.getApprovedPRs(page.skip, page.limit, appliedSearch);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setPrs(list);
      // From the X-Total-Count header, via api.js.
      setTotalCount(res?.totalCount != null ? res.totalCount : list.length);
    } catch (e) {
      console.error('Failed to fetch PRs', e);
      setPrs([]);
    } finally {
      setLoading(false);
    }
  }, [isRejectedReturned, page, appliedSearch, searchVersion]);

  useEffect(() => { fetchPRs(); }, [fetchPRs]);

  // Switching between Approved and Rejected/Returned or a new search starts at page one.
  useEffect(() => {
    setPage((p) => (p.skip === 0 ? p : { ...p, skip: 0 }));
  }, [isRejectedReturned, appliedSearch]);

  const triggerSearch = () => {
    setAppliedSearch(search.trim());
    setSearchVersion((v) => v + 1);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  if (loading) {
    return (
      <RNLLoader
        title={`Loading ${isRejectedReturned ? 'Rejected & Returned' : 'Approved'} Requisitions...`}
        subtitle="Fetching system data"
      />
    );
  }

  if (viewingPrId && viewingInstanceId) {
    return (
      <PRDetailsView
        prId={viewingPrId}
        instanceId={viewingInstanceId}
        onBack={() => {
          api.clearApiCache();
          setViewingPrId(null);
          setViewingInstanceId(null);
          fetchPRs();
        }}
      />
    );
  }

  const uniqueDepartments = Array.from(
    new Set(prs.map((p) => p.department_name || p.department?.name || p.requested_department?.name).filter(Boolean))
  );

  const uniqueProjects = Array.from(
    new Set(prs.map((p) => p.project_name || p.items?.[0]?.project_name).filter(Boolean))
  );

  const uniqueRequesters = Array.from(
    new Set(prs.map((p) =>
      p.requested_by_name ||
      p.requested_by?.full_name ||
      (p.requested_by ? `${p.requested_by.first_name || ''} ${p.requested_by.last_name || ''}`.trim() : '')
    ).filter(Boolean))
  );

  const getOutcomeBadge = (raw) => {
    const s = (raw || '').toUpperCase();
    if (s === 'REJECTED') return { label: 'REJECTED', bg: '#fee2e2', color: '#ef4444' };
    if (s === 'RETURNED') return { label: 'RETURNED', bg: '#ffedd5', color: '#f97316' };
    if (s === 'APPROVED') return { label: 'APPROVED', bg: '#dcfce7', color: '#16a34a' };
    return { label: s || '—', bg: '#f1f5f9', color: '#64748b' };
  };

  const getPriorityBadgeStyle = (prio) => {
    const p = (prio || 'normal').toLowerCase();
    if (p === 'urgent') return { color: '#ef4444', bg: '#fee2e2', border: '#fca5a5' };
    if (p === 'high') return { color: '#d97706', bg: '#fef3c7', border: '#fcd34d' };
    return { color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' };
  };

  const filteredPrs = prs.filter((p) => {
    const deptName = p.department_name || p.department?.name || p.requested_department?.name || '';
    const reqName =
      p.requested_by_name ||
      p.requested_by?.full_name ||
      (p.requested_by ? `${p.requested_by.first_name || ''} ${p.requested_by.last_name || ''}`.trim() : '');
    const matchesStatus = !isRejectedReturned || !statusFilter || (p.request_status || '').toUpperCase() === statusFilter;
    const matchesDept = !deptFilter || deptName === deptFilter;
    const prProject = p.project_name || p.items?.[0]?.project_name || '';
    const matchesProject = !projectFilter || prProject === projectFilter;
    const matchesRequester = !requesterFilter || reqName === requesterFilter;
    return matchesStatus && matchesDept && matchesProject && matchesRequester;
  });

  const sortedPrs = sortRows(filteredPrs, sortState, (pr, key) => {
    switch (key) {
      case '_date':
        return pr.required_date || pr.created_at?.split('T')[0] || '';
      case '_requester':
        return pr.requested_by_name
          || (pr.requested_by ? `${pr.requested_by.first_name || ''} ${pr.requested_by.last_name || ''}`.trim() : '')
          || '';
      case '_department': return pr.department_name || '';
      case '_type': return pr.request_type_name || '';
      case '_project': {
        const many = pr.items?.length > 1
          && new Set(pr.items.map((i) => i.project_id)).size > 1;
        return many ? 'Multiple Projects'
          : (pr.project_name || pr.items?.[0]?.project_name || '');
      }
      case '_priority': return pr.priority_status || pr.items?.[0]?.priority_status || '';
      case '_lines': return pr.items?.length || 0;
      case '_actionedBy': return pr.outcome_by || '';
      case '_actionedAt': return pr.outcome_at || '';
      // total_estimated_cost arrives as a string from the API; Number() keeps
      // ₦100,000 above ₦9,000 instead of below it.
      case 'total_estimated_cost': return Number(pr.total_estimated_cost) || 0;
      default: return pr[key];
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {isRejectedReturned
          ? 'System-wide list of requisitions rejected or returned at the Approval Queue.'
          : 'System-wide list of fully approved requisitions, across every requester and department.'}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, maxWidth: '30%', display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              type="text"
              placeholder="Search by ref or title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                fontSize: 13,
                border: '1.5px solid #cbd5e1',
                borderRadius: 8,
                outline: 'none',
                background: 'var(--bg-input, #fff)',
                color: 'var(--text-primary, #1e293b)',
              }}
            />
          </div>
          <button
            onClick={triggerSearch}
            style={{
              padding: '8px 20px',
              background: '#0b318b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Search
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          {isRejectedReturned && (
            <div style={{ width: 170, flexShrink: 0 }}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: 'All Outcomes' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'RETURNED', label: 'Returned' },
                ]}
              />
            </div>
          )}
          <div style={{ width: 170, flexShrink: 0 }}>
            <Select
              value={deptFilter}
              onChange={setDeptFilter}
              options={[{ value: '', label: 'All Departments' }, ...uniqueDepartments.map((d) => ({ value: d, label: d }))]}
            />
          </div>
          <div style={{ width: 170, flexShrink: 0 }}>
            <Select
              value={projectFilter}
              onChange={setProjectFilter}
              options={[{ value: '', label: 'All Projects' }, ...uniqueProjects.map((p) => ({ value: p, label: p }))]}
            />
          </div>
          <div style={{ width: 170, flexShrink: 0 }}>
            <Select
              value={requesterFilter}
              onChange={setRequesterFilter}
              options={[{ value: '', label: 'All Requesters' }, ...uniqueRequesters.map((r) => ({ value: r, label: r }))]}
            />
          </div>
        </div>
      </div>

      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {filteredPrs.length} requisitions found
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ border: '1px solid black', borderBottom: 'none', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        <div ref={viewport.ref} style={{ overflowX: 'auto' }}>
          <style>{`
            .outcome-table th, .outcome-table td { border: 1px solid black; }
          `}</style>
          <table className="outcome-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '1px solid black' }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <SortTh label="REF" sortKey="pr_number" state={sortState} style={{ width: 140 }} />
                <SortTh label="DATE" sortKey="_date" state={sortState} />
                <SortTh label="REQUESTER" sortKey="_requester" state={sortState} />
                <SortTh label="DEPARTMENT" sortKey="_department" state={sortState} style={{ minWidth: 150 }} />
                <SortTh label="TYPE" sortKey="_type" state={sortState} style={{ minWidth: 130 }} />
                <SortTh label="PROJECT" sortKey="_project" state={sortState} style={{ minWidth: 160 }} />
                <SortTh label="PRIORITY" sortKey="_priority" state={sortState} />
                <SortTh label="LINES" sortKey="_lines" state={sortState} />
                <SortTh label="EST. AMOUNT" sortKey="total_estimated_cost" state={sortState} />
                {isRejectedReturned ? (
                  <>
                    <SortTh label="OUTCOME" sortKey="request_status" state={sortState} />
                    <SortTh label="REASON" sortKey="outcome_reason" state={sortState} style={{ minWidth: 180 }} />
                    <SortTh label="BY" sortKey="_actionedBy" state={sortState} />
                    <SortTh label="DATE ACTIONED" sortKey="_actionedAt" state={sortState} />
                  </>
                ) : (
                  <SortTh label="STATUS" sortKey="request_status" state={sortState} />
                )}
                {/* Icons, so the space goes to Project and Department,
                    which were wrapping onto three lines. */}
                <SortTh label="ACTIONS" style={{ minWidth: 110, width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {sortedPrs.map((pr, idx) => {
                const prId = pr.id || pr.purchase_request_id;
                const prProjectName = pr.project_name || pr.items?.[0]?.project_name || '—';
                const isMultipleProjects = pr.items?.length > 1 && new Set(pr.items.map((i) => i.project_id)).size > 1;
                const outcome = getOutcomeBadge(isRejectedReturned ? pr.request_status : 'approved');
                const pStyle = getPriorityBadgeStyle(pr.priority_status || pr.items?.[0]?.priority_status);

                const isExpanded = !!expandedRows[prId];

                return (
                  <React.Fragment key={prId}>
                  <tr
                    onClick={() => setExpandedRows((prev) => ({ ...prev, [prId]: !prev[prId] }))}
                    style={{
                      background: isExpanded ? '#dbeafe' : (idx % 2 === 0 ? '#fff' : '#f8fafc'),
                      cursor: 'pointer',
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: 700, color: '#0b318bff', whiteSpace: 'nowrap', userSelect: 'none' }}>
                      <span style={{ display: 'inline-block', width: 14, marginRight: 6, color: '#64748b' }}>{isExpanded ? '▼' : '▶'}</span>
                      {pr.pr_number || 'DRAFT'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pr.required_date || pr.created_at?.split('T')[0] || '—'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {pr.requested_by_name || (pr.requested_by ? `${pr.requested_by.first_name || ''} ${pr.requested_by.last_name || ''}`.trim() : '') || '—'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pr.department_name || '—'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pr.request_type_name || '—'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{isMultipleProjects ? 'Multiple Projects' : prProjectName}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span
                        style={{
                          color: pStyle.color, background: pStyle.bg, border: `1px solid ${pStyle.border}`,
                          padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          display: 'inline-block', textTransform: 'capitalize', whiteSpace: 'nowrap',
                        }}
                      >
                        {pr.priority_status || pr.items?.[0]?.priority_status || 'Normal'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>{pr.items?.length || 0}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>₦{fmt(pr.total_estimated_cost)}</td>
                    {isRejectedReturned ? (
                      <>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span
                            style={{
                              background: outcome.bg, color: outcome.color, border: `1px solid ${outcome.color}40`,
                              padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                              display: 'inline-block', whiteSpace: 'nowrap',
                            }}
                          >
                            {outcome.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontStyle: 'italic', color: '#475569', maxWidth: 220 }}>
                          {pr.outcome_reason ? `"${pr.outcome_reason}"` : '—'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{pr.outcome_by || '—'}</td>
                        <td style={{ padding: '12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {pr.outcome_at ? new Date(pr.outcome_at).toLocaleDateString() : '—'}
                        </td>
                      </>
                    ) : (
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span
                          style={{
                            background: outcome.bg, color: outcome.color, border: `1px solid ${outcome.color}40`,
                            padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                            display: 'inline-block', whiteSpace: 'nowrap',
                          }}
                        >
                          {outcome.label}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '12px' }}>
                      {/* A flex row rather than textAlign plus a margin: with
                          two buttons in a narrow column the old spacing left
                          them hanging off-centre, and this wraps cleanly when
                          the column is too tight for one line. */}
                      <div style={{
                        display: 'flex', gap: 6, flexWrap: 'wrap',
                        justifyContent: 'center', alignItems: 'center',
                      }}>
                      <ActionIcon
                        title="View Requisition"
                        color="#0b318bff"
                        onClick={(e) => {
                          // The whole row toggles the expander, so this has to
                          // stop the bubble or View would expand it on the way out.
                          e.stopPropagation();
                          setViewingPrId(prId);
                          setViewingInstanceId(pr.workflow_instance_id);
                        }}
                      >
                        <EyeIcon />
                      </ActionIcon>

                      {/* Approved list only. Edits the record, never the
                          workflow — the edit page says so at the top. */}
                      {!isRejectedReturned && (
                        <ActionIcon
                          title="Edit Requisition"
                          color="#0b318bff"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/enterprise-requisition/edit/${prId}?from=approved-requisitions`);
                          }}
                        >
                          <PencilIcon />
                        </ActionIcon>
                      )}

                      {/* Only a REJECTED request is stranded. A returned one is
                          already back with someone who can act on it, so it
                          needs no recall. */}
                      {String(pr.request_status || '').toUpperCase() === 'REJECTED'
                        && pr.workflow_instance_id && (
                        <ActionIcon
                          title="Recall Requisition"
                          color="#b45309"
                          onClick={(e) => { e.stopPropagation(); setRecallTarget(pr); }}
                        >
                          <RecallIcon />
                        </ActionIcon>
                      )}

                      {/* The approved list. Recalling here reopens the Approval
                          Queue step, but any payment voucher raised against the
                          requisition has to go first — which is what the
                          preview is for. */}
                      {!isRejectedReturned && pr.workflow_instance_id && (
                        <ActionIcon
                          title="Recall Requisition"
                          color="#b45309"
                          onClick={(e) => { e.stopPropagation(); openApprovedRecall(pr); }}
                        >
                          <RecallIcon />
                        </ActionIcon>
                      )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={isRejectedReturned ? 14 : 11} style={{ padding: 0, border: 'none' }}>
                        <PinnedDetail width={viewport.width}>
                          <RequisitionExpandedDetail pr={pr} />
                        </PinnedDetail>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {filteredPrs.length === 0 && (
                <tr>
                  <td colSpan={isRejectedReturned ? 14 : 11} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                    No requisitions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </Card>
      <div style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', background: 'var(--bg-card)' }}>
        <Pagination
          total={totalCount}
          skip={page.skip}
          limit={page.limit}
          onChange={setPage}
        />
      </div>
      </div>

      {recallTarget && (
        <Modal title="Recall this rejected requisition?" onClose={() => setRecallTarget(null)} width={560}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5, lineHeight: 1.6 }}>
            <div>
              <strong>{recallTarget.pr_number}</strong> — {recallTarget.title || '—'}
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              It goes back into the approval queue exactly where it was rejected, so it
              can be acted on again — returned for correction, for instance.
            </div>
            {recallTarget.outcome_reason && (
              <div style={{
                background: 'var(--bg-hover)', border: '1px solid var(--border-light)',
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 4 }}>
                  THE REJECTION COMMENT
                </div>
                <div style={{ fontStyle: 'italic' }}>"{recallTarget.outcome_reason}"</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                  This is cleared from the step so it reopens clean, and kept in the
                  approval history. Paste it into the Return dialog if it is still the
                  feedback you want to give.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setRecallTarget(null)} disabled={recalling}>Cancel</Btn>
              <Btn onClick={recall} loading={recalling}>Recall to the queue</Btn>
            </div>
          </div>
        </Modal>
      )}
      {approvedTarget && (
        <Modal
          title="Recall this approved requisition?"
          onClose={() => { setApprovedTarget(null); setPlan(null); }}
          width={640}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5, lineHeight: 1.6 }}>
            <div>
              <strong>{approvedTarget.pr_number}</strong> — {approvedTarget.title || '—'}
            </div>

            {planLoading && (
              <div style={{ color: 'var(--text-muted)' }}>Checking for payment vouchers…</div>
            )}

            {!planLoading && plan && (
              <>
                {/* What the Voucher engine has already done with it. This is
                    the whole reason the approved recall has a preview at all. */}
                <div style={{
                  background: plan.voucher_count ? '#fff7ed' : 'var(--bg-hover)',
                  border: `1px solid ${plan.voucher_count ? '#fdba74' : 'var(--border-light)'}`,
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 4 }}>
                    PAYMENT VOUCHERS
                  </div>
                  <div>{plan.message}</div>
                </div>

                {plan.voucher_count > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 6 }}>
                      VOUCHERS THAT WOULD BE DELETED
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                            <th style={{ padding: '4px 8px' }}>VOUCHER</th>
                            <th style={{ padding: '4px 8px' }}>STATUS</th>
                            <th style={{ padding: '4px 8px', textAlign: 'right' }}>NET PAYABLE</th>
                            <th style={{ padding: '4px 8px' }}>FLAGS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.vouchers.map((v) => (
                            <tr key={v.voucher_id} style={{ borderTop: '1px solid var(--border-light)' }}>
                              <td style={{ padding: '5px 8px', fontWeight: 700 }}>{v.voucher_no}</td>
                              <td style={{ padding: '5px 8px' }}>{String(v.status || '').toUpperCase()}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right' }}>₦{fmt(v.net_payable)}</td>
                              <td style={{ padding: '5px 8px', color: '#b45309' }}>
                                {[v.released_to_expense_register && 'released', v.is_advance && 'advance']
                                  .filter(Boolean).join(', ') || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {plan.deleted?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 6 }}>
                      RECORDS THAT GO WITH THEM
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {plan.deleted.map((d) => (
                        <li key={d.label}><strong>{d.count}</strong> {d.label.toLowerCase()}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Side effects that land outside this requisition. Spelled out
                    and gated behind a deliberate tick rather than folded into
                    the list above, where they would read as routine. */}
                {plan.warnings?.map((w) => (
                  <div key={w.kind} style={{
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    borderRadius: 8, padding: '10px 12px',
                  }}>
                    <div style={{ fontWeight: 800, color: '#b91c1c', marginBottom: 4 }}>
                      ⚠ {w.headline}
                    </div>
                    <div style={{ color: '#7f1d1d' }}>{w.detail}</div>
                    {w.kind === 'released_to_expense_register' && (
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#7f1d1d' }}>
                        {w.items.map((i) => (
                          <li key={i.voucher_no}>{i.voucher_no} — ₦{fmt(i.net_payable)}</li>
                        ))}
                      </ul>
                    )}
                    {w.kind === 'advance_recovery' && (
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#7f1d1d' }}>
                        {w.items.map((i, idx) => (
                          <li key={idx}>
                            ₦{fmt(i.amount)} recovered from {i.advance_voucher_no} by{' '}
                            {i.recovering_voucher_no} — <strong>{i.surviving_voucher_no}</strong> keeps
                            its record but loses this history
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}

                {plan.requires_confirmation && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={acceptedWarnings}
                      onChange={(e) => setAcceptedWarnings(e.target.checked)}
                      style={{ marginTop: 4 }}
                    />
                    <span>
                      I understand these records are deleted permanently, including the
                      effects outside this requisition.
                    </span>
                  </label>
                )}

                {!plan.can_recall && (
                  <div style={{ color: '#b91c1c', fontWeight: 700 }}>{plan.blocked}</div>
                )}
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn
                variant="ghost"
                onClick={() => { setApprovedTarget(null); setPlan(null); }}
                disabled={recalling}
              >
                Cancel
              </Btn>
              <Btn
                onClick={recallApproved}
                loading={recalling}
                disabled={
                  planLoading || !plan || !plan.can_recall
                  || (plan.requires_confirmation && !acceptedWarnings)
                }
              >
                {plan?.voucher_count
                  ? `Delete ${plan.voucher_count} voucher(s) and recall`
                  : 'Recall to the Approval Queue'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
      {recallDone && (
        <Modal title="Recalled" onClose={() => setRecallDone(null)} width={560}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5, lineHeight: 1.6 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#dcfce7', border: '1px solid #16a34a40',
              borderRadius: 10, padding: '12px 14px',
            }}>
              <span style={{
                width: 26, height: 26, borderRadius: 999, background: '#16a34a', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, flexShrink: 0,
              }}>✓</span>
              <span style={{ fontWeight: 700, color: '#166534' }}>
                {recallDone.pr_number} is back at {recallDone.step_name || 'the approval queue'}.
              </span>
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              It can be acted on again — returned for correction, for instance. It has
              left this list and is back in the queue.
            </div>
            {/* Only the approved recall deletes anything, so this block simply
                does not appear for a recalled rejection. */}
            {recallDone.vouchers_deleted?.length > 0 && (
              <div style={{
                background: '#fff7ed', border: '1px solid #fdba74',
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 4 }}>
                  PAYMENT VOUCHERS DELETED
                </div>
                <div>{recallDone.vouchers_deleted.join(', ')}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                  A fresh voucher can be raised once the requisition is approved again.
                </div>
              </div>
            )}
            {recallDone.original_comment && (
              <div style={{
                background: 'var(--bg-hover)', border: '1px solid var(--border-light)',
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 4 }}>
                  THE ORIGINAL REJECTION COMMENT — KEPT IN THE HISTORY
                </div>
                <div style={{ fontStyle: 'italic' }}>"{recallDone.original_comment}"</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                  Paste this into the Return dialog if it is still the feedback you want to give.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn onClick={() => setRecallDone(null)}>Done</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
